import { Project, ProjectAccessMode, ProjectMember } from '../contexts/ProjectContext'
import { Permission } from '../utils/permissions'
import { api } from './api'

export interface UpdateMemberPermissionsRequest {
  projectId: string
  memberEmail: string
  customPermissions: Partial<Permission>
}

export interface AddMemberRequest {
  projectId: string
  email: string
  role: string
  addedBy: string
}

export interface UpdateMemberRoleRequest {
  projectId: string
  email: string
  role: string
}

export interface CloneProjectRequest {
  name: string
  description?: string
}

export interface ProjectAccessMetadata {
  isDemoTemplate?: boolean
  accessMode?: ProjectAccessMode
}

export interface AdminCheckResponse {
  email: string
  isGlobalAdmin: boolean
}

class ProjectAPI {
  // 모든 프로젝트 조회
  // Phase 1 / P4 (2026-06-03): optional ``orgId`` filters by organization.
  // When omitted the backend falls back to the ``active_org_id`` JWT claim.
  async getProjects(orgId?: string): Promise<Project[]> {
    try {
      const response = await api.get('/api/projects', {
        params: orgId ? { orgId } : undefined,
      })
      return response.data
    } catch (error) {
      console.error('프로젝트 조회 오류:', error)
      throw error
    }
  }

  // 특정 프로젝트 조회
  async getProject(projectId: string): Promise<Project> {
    try {
      const response = await api.get(`/api/projects/${projectId}`)
      return response.data
    } catch (error) {
      console.error('프로젝트 조회 오류:', error)
      throw error
    }
  }

  // 새 프로젝트 생성
  // Phase 1 / P4 (2026-06-03): if ``project.organizationId`` is provided we
  // forward it to the backend ``ProjectCreateRequest`` so the project is
  // bound to the chosen org. Otherwise the backend falls back to the
  // caller's personal org (idempotent provisioning).
  async createProject(
    project: Project & { organizationId?: string | null }
  ): Promise<Project> {
    try {
      const payload: Record<string, unknown> = {
        name: project.name,
        description: project.description,
      }
      if (project.organizationId) {
        payload.organizationId = project.organizationId
      }
      const response = await api.post('/api/projects', payload)
      return response.data
    } catch (error) {
      console.error('프로젝트 생성 오류:', error)
      throw error
    }
  }

  // 멤버 세부 권한 업데이트
  async updateMemberPermissions(request: UpdateMemberPermissionsRequest): Promise<unknown> {
    try {
      const response = await api.put(
        `/api/projects/${request.projectId}/members/permissions`,
        request
      )
      return response.data
    } catch (error) {
      console.error('권한 업데이트 오류:', error)
      throw error
    }
  }

  // 멤버 추가
  async addMember(request: AddMemberRequest): Promise<unknown> {
    try {
      const response = await api.post(
        `/api/projects/${request.projectId}/members`,
        request
      )
      return response.data
    } catch (error) {
      console.error('멤버 추가 오류:', error)
      throw error
    }
  }

  // 멤버 역할 변경
  async updateMemberRole(request: UpdateMemberRoleRequest): Promise<unknown> {
    try {
      const response = await api.put(
        `/api/projects/${request.projectId}/members/role`,
        request
      )
      return response.data
    } catch (error) {
      console.error('역할 변경 오류:', error)
      throw error
    }
  }

  // 멤버 제거
  async removeMember(projectId: string, email: string): Promise<unknown> {
    try {
      const response = await api.delete(
        `/api/projects/${projectId}/members/${encodeURIComponent(email)}`
      )
      return response.data
    } catch (error) {
      console.error('멤버 제거 오류:', error)
      throw error
    }
  }

  // 프로젝트 정보 업데이트
  async updateProject(projectId: string, updates: Partial<Project>): Promise<unknown> {
    try {
      const response = await api.put(`/api/projects/${projectId}`, updates)
      return response.data
    } catch (error) {
      console.error('프로젝트 업데이트 오류:', error)
      throw error
    }
  }

  // 프로젝트 삭제
  async deleteProject(projectId: string): Promise<unknown> {
    try {
      const response = await api.delete(`/api/projects/${projectId}`)
      return response.data
    } catch (error) {
      console.error('프로젝트 삭제 오류:', error)
      throw error
    }
  }

  // ============ 프로젝트 복제 API ============
  // Phase G (2026-06-03): 샘플 프로젝트 토글/리스트 API 는 폐기.
  // Use case 카탈로그 (`/api/use-cases`) 로 대체됨.

  // 프로젝트 복제
  async cloneProject(projectId: string, request: CloneProjectRequest, userEmail: string): Promise<Project> {
    try {
      // X-User-Email 헤더는 api 인터셉터에서 자동 추가됨
      const response = await api.post(`/api/projects/${projectId}/clone`, request)
      return response.data
    } catch (error) {
      console.error('프로젝트 복제 오류:', error)
      const err = error as { response?: { data?: { detail?: string } }; message?: string }
      const errorMessage = err.response?.data?.detail || `프로젝트 복제 실패: ${err.message}`
      throw new Error(errorMessage)
    }
  }

  // 글로벌 관리자 여부 확인
  async checkAdminStatus(): Promise<AdminCheckResponse> {
    try {
      const response = await api.get('/api/projects/admin/check')
      return response.data
    } catch (error) {
      console.error('관리자 상태 확인 오류:', error)
      throw error
    }
  }
}

export const projectAPI = new ProjectAPI()
export default projectAPI
